import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Injector,
  PLATFORM_ID,
  afterNextRender,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChartColumnDef } from '../chart-table/chart-table.types';
import { TableKpiCardItem } from './table-customizer.types';
import { ButtonComponent, ButtonSize, ButtonVariant } from '../button/button.component';
import { SavedView } from '../../models/saved-view.model';
import { VIEW_URL_PARAM, viewUrlSlug } from '../../pages/overview/artists/artist-rankings-url.util';
import { FlipPositionDirective } from '../../utils/flip-position.directive';

export type SaveViewPayload = { mode: 'create'; name: string } | { mode: 'update'; id: number };

export interface TablePeriodOption {
  id: string;
  label: string;
}

export const DUPLICATE_VIEW_NAME_MESSAGE = 'A view with this name already exists';

export function isViewNameTaken(views: SavedView[], name: string, exceptId?: number): boolean {
  const normalized = name.trim().toLowerCase();
  return views.some((v) => v.id !== exceptId && v.name.trim().toLowerCase() === normalized);
}

export function suggestViewName(views: SavedView[]): string {
  let n = views.length + 1;
  while (isViewNameTaken(views, `View ${n}`)) n++;
  return `View ${n}`;
}

@Component({
  selector: 'app-table-customizer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ButtonComponent, FlipPositionDirective],
  templateUrl: './table-customizer.component.html',
})
export class TableCustomizerComponent {
  private elementRef = inject(ElementRef);
  private platformId = inject(PLATFORM_ID);
  private injector = inject(Injector);

  // Two-way bindings for columns and KPI cards
  columns = model<ChartColumnDef<any>[]>([]);
  kpiCards = model<TableKpiCardItem[]>([]);
  isOpen = model<boolean>(false);

  // Configuration inputs
  buttonVariant = input<ButtonVariant>('naked-primary');
  buttonSize = input<ButtonSize>('small');
  align = input<'left' | 'right'>('right');
  disabled = input<boolean>(false);
  buttonLabel = input<string>('Customize');

  activeView = input<SavedView | null>(null);
  views = input<SavedView[]>([]);
  isAuthenticated = input<boolean>(false);
  isDirty = input<boolean>(false);
  initialNamingDraft = input<string | null>(null);
  periodOptions = input<TablePeriodOption[]>([]);
  activePeriodId = input<string | null>(null);

  selectView = output<SavedView | null>();
  renameView = output<{ id: number; name: string }>();
  deleteView = output<number>();
  saveView = output<SaveViewPayload>();
  resetLayout = output<void>();
  saveRequiresAuth = output<string>();
  namingDraftConsumed = output<void>();
  periodChange = output<string>();

  draggedIndex = signal<number | null>(null);
  dropTargetIndex = signal<number | null>(null);
  dropPosition = signal<'above' | 'below' | null>(null);

  isViewDropdownOpen = signal(false);
  renamingViewId = signal<number | null>(null);
  renameDraft = signal<string>('');
  renameError = signal<string | null>(null);
  copiedViewId = signal<number | null>(null);

  isNamingNewView = signal(false);
  nameDraft = signal<string>('');
  nameError = signal<string | null>(null);

  triggerButtonEl = viewChild<ElementRef<HTMLElement>>('triggerWrapper');
  viewSelectorEl = viewChild<ElementRef<HTMLElement>>('viewSelector');
  renameInput = viewChild<ElementRef<HTMLInputElement>>('renameInput');
  nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');

  constructor() {
    effect(() => {
      const draft = this.initialNamingDraft();
      const open = this.isOpen();
      if (draft === null || !open) return;
      untracked(() => {
        this.isNamingNewView.set(true);
        this.nameDraft.set(draft);
        this.nameError.set(isViewNameTaken(this.views(), draft) ? DUPLICATE_VIEW_NAME_MESSAGE : null);
        this.focusAfterRender(() => this.nameInput());
        this.namingDraftConsumed.emit();
      });
    });

    effect(() => {
      if (this.isOpen()) return;
      untracked(() => {
        this.draggedIndex.set(null);
        this.dropTargetIndex.set(null);
        this.dropPosition.set(null);
        this.isViewDropdownOpen.set(false);
        this.renamingViewId.set(null);
        this.renameError.set(null);
        this.isNamingNewView.set(false);
        this.nameError.set(null);
      });
    });
  }

  private focusAfterRender(ref: () => ElementRef<HTMLInputElement> | undefined): void {
    afterNextRender(() => ref()?.nativeElement.focus(), { injector: this.injector });
  }

  triggerElement(): HTMLElement | null {
    return this.triggerButtonEl()?.nativeElement ?? null;
  }

  toggleOpen(event?: MouseEvent): void {
    if (this.disabled()) return;
    if (event) {
      event.stopPropagation();
    }
    this.isOpen.update((prev) => !prev);
  }

  close(): void {
    if (this.isOpen()) {
      this.isOpen.set(false);
      this.draggedIndex.set(null);
      this.dropTargetIndex.set(null);
      this.dropPosition.set(null);
      this.isViewDropdownOpen.set(false);
      this.renamingViewId.set(null);
      this.isNamingNewView.set(false);
    }
  }

  toggleColumn(column: ChartColumnDef<any>, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (column.immutable) {
      return;
    }

    const currentList = this.columns();
    const targetId = column.id;
    const updated = currentList.map((col) => {
      if (col.id === targetId) {
        const currentVisible = col.visible !== false;
        return {
          ...col,
          visible: !currentVisible,
        };
      }
      return col;
    });

    this.columns.set(updated);
  }

  selectPeriod(option: TablePeriodOption, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (option.id === this.activePeriodId()) return;
    this.periodChange.emit(option.id);
  }

  toggleKpi(kpi: TableKpiCardItem, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    const currentList = this.kpiCards();
    const updated = currentList.map((item) => {
      if (item.id === kpi.id) {
        return {
          ...item,
          visible: !item.visible,
        };
      }
      return item;
    });

    this.kpiCards.set(updated);
  }

  // HTML5 Drag & Drop handlers
  onDragStart(index: number, event: DragEvent): void {
    if (this.columns()[index]?.immutable) {
      event.preventDefault();
      return;
    }
    this.draggedIndex.set(index);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', index.toString());
    }
  }

  onDragOver(index: number, event: DragEvent): void {
    event.preventDefault();
    if (this.columns()[index]?.immutable) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'none';
      }
      return;
    }
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }

    const currentTarget = event.currentTarget as HTMLElement | null;
    let position: 'above' | 'below' = 'above';
    if (currentTarget && typeof event.clientY === 'number') {
      const rect = currentTarget.getBoundingClientRect();
      const relY = event.clientY - rect.top;
      position = relY < rect.height / 2 ? 'above' : 'below';
    }

    if (this.dropTargetIndex() !== index || this.dropPosition() !== position) {
      this.dropTargetIndex.set(index);
      this.dropPosition.set(position);
    }
  }

  onDragLeave(index: number, event?: DragEvent): void {
    if (event) {
      const currentTarget = event.currentTarget as HTMLElement | null;
      const relatedTarget = event.relatedTarget as Node | null;
      if (currentTarget && relatedTarget && currentTarget.contains(relatedTarget)) {
        return; // Dragging over children within the same row: do not flicker
      }
    }
    if (this.dropTargetIndex() === index) {
      this.dropTargetIndex.set(null);
      this.dropPosition.set(null);
    }
  }

  onDrop(targetIndex: number, event: DragEvent): void {
    event.preventDefault();
    const srcIndex = this.draggedIndex();
    if (
      srcIndex === null ||
      srcIndex === targetIndex ||
      this.columns()[srcIndex]?.immutable ||
      this.columns()[targetIndex]?.immutable
    ) {
      this.draggedIndex.set(null);
      this.dropTargetIndex.set(null);
      this.dropPosition.set(null);
      return;
    }

    const items = [...this.columns()];
    const [removed] = items.splice(srcIndex, 1);

    // Determine whether we want to insert above or below the target item
    const position = this.dropPosition() ?? (srcIndex < targetIndex ? 'below' : 'above');

    let insertIndex: number;
    if (srcIndex < targetIndex) {
      insertIndex = position === 'above' ? targetIndex - 1 : targetIndex;
    } else {
      insertIndex = position === 'above' ? targetIndex : targetIndex + 1;
    }

    // Safety: ensure insertIndex never precedes immutable columns
    const firstMutableIndex = items.findIndex((col) => !col.immutable);
    if (firstMutableIndex !== -1 && insertIndex < firstMutableIndex) {
      insertIndex = firstMutableIndex;
    }

    items.splice(insertIndex, 0, removed);
    this.columns.set(items);

    this.draggedIndex.set(null);
    this.dropTargetIndex.set(null);
    this.dropPosition.set(null);
  }

  onDragEnd(): void {
    this.draggedIndex.set(null);
    this.dropTargetIndex.set(null);
    this.dropPosition.set(null);
  }

  toggleViewDropdown(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.isViewDropdownOpen.update((v) => !v);
  }

  selectDefaultLayout(): void {
    this.isViewDropdownOpen.set(false);
    this.selectView.emit(null);
  }

  selectSavedView(view: SavedView): void {
    this.isViewDropdownOpen.set(false);
    this.selectView.emit(view);
  }

  startRename(view: SavedView, event: MouseEvent): void {
    event.stopPropagation();
    this.renamingViewId.set(view.id);
    this.renameDraft.set(view.name);
    this.renameError.set(null);
    this.focusAfterRender(() => this.renameInput());
  }

  onRenameInput(value: string): void {
    this.renameDraft.set(value);
    this.renameError.set(null);
  }

  confirmRename(): void {
    const id = this.renamingViewId();
    if (id === null) return;
    const name = this.renameDraft().trim();
    const current = this.views().find((v) => v.id === id);
    if (name.length === 0 || name === current?.name) {
      this.cancelRename();
      return;
    }
    if (isViewNameTaken(this.views(), name, id)) {
      this.renameError.set(DUPLICATE_VIEW_NAME_MESSAGE);
      this.focusAfterRender(() => this.renameInput());
      return;
    }
    this.renamingViewId.set(null);
    this.renameError.set(null);
    this.renameView.emit({ id, name });
  }

  cancelRename(): void {
    this.renamingViewId.set(null);
    this.renameError.set(null);
  }

  copyViewLink(view: SavedView, event: MouseEvent): void {
    event.stopPropagation();
    if (!isPlatformBrowser(this.platformId)) return;

    const link = `${window.location.origin}${window.location.pathname}?${VIEW_URL_PARAM}=${encodeURIComponent(viewUrlSlug(view))}`;
    navigator.clipboard.writeText(link).then(() => {
      this.copiedViewId.set(view.id);
      setTimeout(() => {
        if (this.copiedViewId() === view.id) this.copiedViewId.set(null);
      }, 1500);
    });
  }

  confirmDeleteView(view: SavedView, event: MouseEvent): void {
    event.stopPropagation();
    this.deleteView.emit(view.id);
  }

  suggestedViewName(): string {
    return suggestViewName(this.views());
  }

  onResetClick(): void {
    this.resetLayout.emit();
  }

  onSaveClick(): void {
    if (!this.isAuthenticated()) {
      this.saveRequiresAuth.emit(this.suggestedViewName());
      return;
    }

    const active = this.activeView();
    if (active) {
      this.saveView.emit({ mode: 'update', id: active.id });
      return;
    }

    this.isNamingNewView.set(true);
    this.nameDraft.set(this.suggestedViewName());
    this.nameError.set(null);
    this.focusAfterRender(() => this.nameInput());
  }

  onNameInput(value: string): void {
    this.nameDraft.set(value);
    this.nameError.set(null);
  }

  confirmCreateName(): void {
    const name = this.nameDraft().trim() || this.suggestedViewName();
    if (isViewNameTaken(this.views(), name)) {
      this.nameError.set(DUPLICATE_VIEW_NAME_MESSAGE);
      this.focusAfterRender(() => this.nameInput());
      return;
    }
    this.isNamingNewView.set(false);
    this.nameError.set(null);
    this.saveView.emit({ mode: 'create', name });
  }

  cancelCreateName(): void {
    this.isNamingNewView.set(false);
    this.nameDraft.set('');
    this.nameError.set(null);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isViewDropdownOpen() && !this.elementRef.nativeElement.contains(event.target)) {
      this.isViewDropdownOpen.set(false);
    }
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.renamingViewId() !== null) {
      this.cancelRename();
      return;
    }
    if (this.isNamingNewView()) {
      this.cancelCreateName();
      return;
    }
    if (this.isViewDropdownOpen()) {
      this.isViewDropdownOpen.set(false);
      return;
    }
    this.close();
  }
}
